import { Controller } from "@hotwired/stimulus"

// Structured cell-text editor for a native "table" kind element (card #3068)
// -- the "Edit table" modal loaded via DecksController#table_editor.
//
// v1 scope: cell TEXT content only. Style is never sent to the server at all
// (REVISED 2026-08-19 -- an earlier version round-tripped each cell's style
// keys via a data-style attribute so the server could write back the WHOLE
// headers+rows structure; that wholesale write meant editing one cell's text
// froze the WHOLE table's styling as of that save -- a later re-authoring of
// `options` via MCP would then have zero visible effect, ever again, for
// every cell. See the "table" branch's own comment in
// decks/slides/_canvas.html.erb for the full write-up). This controller now
// only ever reads/writes text.
//
// Only DIRTY cells are submitted: each input's current value is compared
// against its own defaultValue (the server-rendered starting text, already
// merged with any prior override -- see DecksController#table_editor /
// decks/table_editor.html.erb). An untouched cell is omitted from the
// payload entirely -- DecksController#update_table_element MERGES the
// submitted sparse map onto whatever was already stored for this element, so
// omitting an untouched cell can never drop a DIFFERENT cell's prior
// override.
export default class extends Controller {
  static targets = ["headerCell", "cell", "error", "saveButton"]
  static values = { deckId: Number, slideIndex: Number, elementIndex: Number }

  async save() {
    this._clearError()

    const cells = {}
    this.headerCellTargets.forEach((input) => {
      if (input.value !== input.defaultValue) cells[`headers.${input.dataset.colIndex}`] = input.value
    })
    this.cellTargets.forEach((input) => {
      if (input.value !== input.defaultValue) cells[`rows.${input.dataset.rowIndex}.${input.dataset.colIndex}`] = input.value
    })

    // No-op guard: nothing changed -- close without a network round trip.
    if (Object.keys(cells).length === 0) {
      this.close()
      return
    }

    this.saveButtonTarget.disabled = true

    try {
      const response = await fetch(this._url(), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({ cells }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error((data.errors || []).join(", ") || `save failed: ${response.status}`)

      this.close()
    } catch (error) {
      this.saveButtonTarget.disabled = false
      this._showError(error.message)
    }
  }

  // Closes the SAME global <dialog id="content_modal"> every WIN picker/form
  // uses -- the native .close() call fires the 'close' event
  // content_modal_controller.js#handleNativeClose already listens for, which
  // clears the frame and restores focus, so nothing bespoke is needed here.
  close() {
    document.getElementById("content_modal")?.close()
  }

  _url() {
    return `/decks/${this.deckIdValue}/element/${this.slideIndexValue}/${this.elementIndexValue}/edit_table`
  }

  _showError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message
  }

  _clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = ""
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
